import './buttons.css';
import { Portal } from 'react-portal';
import PropTypes from 'prop-types';
import React, { useMemo, useRef, useState } from 'react';
import { usePopper } from 'react-popper';

import postal from 'postal';
import Menu, { IfmsMenuItem } from './menu/Menu';
import PrimaryInverseButton from './PrimaryInverseButton';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { replaceJobMenuMetadata } from '../features/jobMenuSlice';
import { replaceScreenStackMetadata } from '../features/screenStackSlice';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { formatDate } from '../dateUtils';
import { FilterStorageVersion } from '../filtering';
import fastStorage from '../fastStorage';
import _ from 'lodash';
import { changeRQEvent } from '../components/common/commonListRenderers';
import filterActiveButton from '../img/iconbutton-filter-tab-selected.svg';
import filterInActiveButton from '../img/iconbutton-filter-tab.svg';
import filterButton from '../img/filter.svg';

interface IStandAloneInfoModel {
  index: number
}

export interface TooltipButtonItem {
  [x: string]: any,
  label: React.ReactNode | string | undefined,
};

function splitMenuItemsIntoSections(tooltipButtonItems: TooltipButtonItem[]): IfmsMenuItem[][][] {
  const result: IfmsMenuItem[][][] = [[[]]]
  tooltipButtonItems.forEach((item, index) => {
    result[result.length - 1][result[result.length - 1].length - 1].push({
      text: item.label,
      selected: false,
      index,
      icon: item?.icon ?? null,
    });
  })
  return result;
}

function renderDateRangeValue(x: any) {
  const parts = x.value ? x.value.split('~') : [];
  const from = parts.length > 0 ? formatDate(parts[0].trim()) : '';
  const to = parts.length > 1 ? formatDate(parts[1].trim()) : '';

  return `${formatDate(from)} ~ ${formatDate(to)}`;
}

function renderValue(x: any) {
  let result = ''

  if (x.type === 'bool')
    result = x.value ? 'Checked' : 'Unchecked';
  else if (x.type === 'date')
    result = renderDateRangeValue(x);
  //Container Tracking Column 검색 시 Tracking Event로만 검색되도록 수정하여
  // Search History가 나올때도 Track Event만 표시되도록 조건 추가
  else if(x.columnId === "trStatus" && x.label === "Container Tracking")
    result = changeRQEvent(x.value, true);
  else
    result = x.value === null ? '' : x.value

  return result;
}

function TooltipButton({
  jobMenuLabel,
  jobMenuUrl,
  jobMenuMetadata,
  standAloneInfo,
  isActiveMenu = false,
  screenFilter,
}: {
  jobMenuLabel: string,
  jobMenuUrl: string,
  jobMenuMetadata?: any,
  standAloneInfo?: IStandAloneInfoModel,
  children?: React.ReactChild | React.ReactNode | string | null | undefined,
  isActiveMenu: boolean,
  screenFilter?: any,
}) {
  const storage = useAppSelector(x => x.storage);
  const channel = postal.channel('ifms-filter');

  const [menuVisible, _setMenuVisible] = useState(false);
  const [referenceElement, setReferenceElement] = useState<HTMLSpanElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrowElement,
        }
      }],
    placement: 'bottom',
  });

  const dispatch = useAppDispatch();
  const filterOrdersStorageKey = `filter-order-${FilterStorageVersion}-${jobMenuUrl}${standAloneInfo ? `-${standAloneInfo.index}` : ''}`;
  const filterOrderStorage = storage[filterOrdersStorageKey];

  const menuContainerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const openRef = useRef(false);

  function getSearchTooltipLabel(filterOrder: any, url: string, screenFilter: any) {
    let result: TooltipButtonItem[] = [];
    if ((!filterOrder || filterOrder.length <= 0) && (screenFilter && (!screenFilter.listType && !screenFilter.listType2 && !screenFilter.listType3 && !screenFilter.listType4)))
      return result;

    if (url === '/common/joblistview/RPCSP/shipmentStatus') {
      if (screenFilter.listType > 0) {
        let filterType = 'Customer';
        result.push({ label: <span style={{ cursor: 'text' }}>{`Filter : ${filterType}`}</span> });  
      }
      if (screenFilter.listType2 > 0) {
        let filterType = '';
        switch (screenFilter.listType2) {
          case 1:
            filterType = 'NOT DEPARTED'
            break;
          case 2:
            filterType = 'VESSEL DEPARTURE'
            break;
          case 3:
            filterType = 'VESSEL ARRIVAL'
            break;
          case 4:
            filterType = 'PICKUP AVAILABLE'
            break;
          case 5:
            filterType = 'GATE OUT'
            break;
          case 6:
            filterType = 'EMPTY CNTR RETURN'
            break;
        }
        result.push({ label: <span style={{ cursor: 'text' }}>{`Filter : ${filterType}`}</span> });  
      }
      if(screenFilter.listType3 > 0){
        result.push({ label: <span style={{ cursor: 'text' }}>{`Filter : Tracking Only`}</span> });  
      }
      
      if (screenFilter.listType4 > 0) {
        let filterType = '';
        switch (screenFilter.listType4) {
          case 1:
            filterType = 'Type - OE'
            break;
          case 2:
            filterType = 'Type - OT'
            break;
        }
        result.push({ label: <span style={{ cursor: 'text' }}>{`Filter : ${filterType}`}</span> });  
      }
    }

    if (url === '/common/joblistview/AICTR/aiCenter') {
      if (screenFilter.listType2 > 0) {
        let filterType = '';
        switch (screenFilter.listType2) {
          case 1:
            filterType = 'B/L'
            break;
          case 2:
            filterType = 'Invoice'
            break;
          case 3:
            filterType = 'Bill'
            break;
          case 4:
            filterType = 'CrDb'
            break;
          case 5:
            filterType = 'ISF'
            break;
        }
        result.push({ label: <span style={{ cursor: 'text' }}>{`Filter : ${filterType}`}</span> });  
      }
    }


    if (filterOrder && filterOrder.length > 0) {
      const tempReverseArray = [...filterOrder].reverse();
      tempReverseArray.slice(-6).map((x: any, i: number) => {
        return result.push({
          label:
            <span style={{ cursor: 'text' }}>
              {`Search ${tempReverseArray.length <= 6 ? i + 1 : tempReverseArray.length - (5 - i)} ${x.label}: `}
              {String(renderValue(x))}
            </span>
        });
      })
    }

    if (isActiveMenu) {
      result.push({ label: '' });
      result.push({ label: <span style={{ display: 'flex', alignItems: 'center', cursor: 'text', color: 'blue', fontWeight: 'bold' }}>CLICK { <img src = {filterButton} />} TO RESET FILTER</span> })
    }

    return result;
  }

  function resetSearch() {
    if (jobMenuUrl.includes('common/joblistview')) {
      const rowSelector = '.data-row-selected';
      const currentRowSelections = document.querySelectorAll(rowSelector);
      if (currentRowSelections && currentRowSelections.length > 0) {
        currentRowSelections[0].classList.remove('data-row-selected');
        currentRowSelections[0].classList.add('data-row-selectable');
      }
    }

    if (jobMenuUrl.includes('/common/joblistview/RPCSP/shipmentStatus')) {
      const keyMatch = _.keys(fastStorage).find(x => x.startsWith('shipStatus-cust-ListFilter'));
      if (keyMatch) {
        delete fastStorage[keyMatch];
      }
      
      const keyMatch2 = _.keys(fastStorage).find(x => x.startsWith('shipStatus-status-ListFilter'));
      if (keyMatch2) {
        delete fastStorage[keyMatch2];
      }

      const keyMatch3 = _.keys(fastStorage).find(x => x.startsWith('shipStatus-tracking-ListFilter'));
      if (keyMatch3) {
        delete fastStorage[keyMatch3];
      }

      const keyMatch4 = _.keys(fastStorage).find(x => x.startsWith('shipStatus-blType-ListFilter'));
      if (keyMatch4) {
        delete fastStorage[keyMatch4];
      }

    }
    if(jobMenuUrl.includes('/common/joblistview/AICTR/aiCenter')){
      const keyMatch = _.keys(fastStorage).find(x => x.startsWith('aiCenter-result-ListFilter'));
      if (keyMatch) {
        delete fastStorage[keyMatch];
      }
    }


    channel.publish('ifms-filter.forceclear');

    if (!standAloneInfo) {
      dispatch(replaceJobMenuMetadata({ url: jobMenuUrl, metadata: jobMenuMetadata }));
      dispatch(replaceScreenStackMetadata({ url: jobMenuUrl, metadata: jobMenuMetadata }));
    }
  }

  function setMenuVisible(v: boolean) {
    openRef.current = v;
    _setMenuVisible(v);
  }

  function onMenuItemSelected(item: IfmsMenuItem, e: React.MouseEvent) {
    //setMenuVisible(false);

    if (!item) {
      resetSearch();
    }
  }

  const appliedPopperStyles = {
    ...styles.popper,
    minWidth: '75px',

    /*paddingTop: 10*/
  };

  const items = getSearchTooltipLabel(filterOrderStorage, jobMenuUrl, screenFilter);

  /* reformat array by separator. This is done because of avoiding changes in tons of files. */
  const formatterItems = useMemo(() => splitMenuItemsIntoSections(items),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items])

  return (
    <>
      <PrimaryInverseButton
        name={`${jobMenuLabel}-resetSearchBtn`}
        ref={buttonRef}
        onMouseEnter={() => setMenuVisible(true)}
        onMouseLeave={() => setMenuVisible(false)}
        size="small"
        style={{ margin: '0px',  padding: '0px'}}
        onClick={() => {
          if (isActiveMenu)
            resetSearch();
        }}
      >
        {/* <FontAwesomeIcon size={'lg'} color={isActiveMenu ? !standAloneInfo ? 'orange' : '#FF6B35' : 'black'} icon={faFilter} /> */}
        <img src = {filterButton} />
        <span ref={setReferenceElement}></span>
      </PrimaryInverseButton>

      <Portal node={document && document.getElementById('ifms-app-bottom')}>
        {menuVisible &&
          <div
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
            onMouseMove={(e) => { e.stopPropagation() }}
            onMouseEnter={() => setMenuVisible(true)}
            onMouseLeave={() => setMenuVisible(false)}
            ref={setPopperElement} style={appliedPopperStyles} {...attributes.popper}>
            <div ref={menuContainerRef}>
              <Menu
                specialMenu='tooltip'
                items={formatterItems}
                mode="vertical"
                onMenuItemSelected={(item, e) => { onMenuItemSelected(item, e) }}
                subMenuStyle={{
                  minWidth: 150,
                  whiteSpace: 'nowrap',
                }}
                menuStyle={(i: number) => ({
                  marginTop: -10,
                  marginBottom: -10
                })}
              />
              <div ref={setArrowElement} style={styles.arrow} />
            </div>
          </div>
        }
      </Portal>
    </>
  );
}

TooltipButton.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    separator: PropTypes.bool,
  })),
};

export default React.memo(TooltipButton);